import { axiosInstance } from "@/config/axios-wrapper";
import { SUGGESTIONS_URL } from "@/config/vars";
import { V2Response } from "@/types/api";
import { Job } from "@/types/job";
import { JobApplication } from "@/types/job_application";
import { JobseekerSearchResult } from "@/types/user";
import { LAST_LOGIN_METHOD_KEY } from "@/utils/lastLoginMethod";
import anyAxios from "axios";

// starred page
export const getStarredDetails = async (id: string, sortVal: string, searchVal: string) => {
  const response = await axiosInstance.get<V2Response<any>>(`/organizations/${id}/favourites`, {
    params: {
      q: searchVal,
      sort: sortVal,
    },
  });
  return response.data.result;
};

// Give Star to candidates
export const giveStarredDetails = async (
  headers: any,
  data: { organizationId: string; userId: string },
) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/organizations/${data.organizationId}/favourites/${data.userId}`,
    null,
    { headers },
  );
  return response.data.result;
};

// Give Star to candidates
export const giveStar = async (data: { organizationId: string; userId: string }) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/organizations/${data.organizationId}/favourites/${data.userId}`,
  );
  return response.data.result;
};

export const deleteStarredDetails = async (id: string, userId: string, headers: any) => {
  const response = await axiosInstance.delete<V2Response<any>>(
    `/organizations/${id}/favourites/${userId}`,
    {
      headers,
    },
  );
  return response.data.result;
};

// job api
/** `result` is an envelope: `{ jobs: [...] }`, not a bare array. */
export const getAllJobs = async (organizationId: string) => {
  const response = await axiosInstance.get<V2Response<{ jobs: Job[] }>>(
    `/organizations/${organizationId}/jobs`,
  );
  return response.data.result;
};

export const deleteSingleJob = async (id: string, headers: any) => {
  const response = await axiosInstance.delete<V2Response<any>>(`jobs/${id}`, headers);
  return response.data.result;
};

// ----------------- [ MEETINGS API ]
// Fetch all meetings
export const getAllMeetings = async (organizationId: string) => {
  const response = await axiosInstance.get<V2Response<any>>(
    `/organizations/${organizationId}/meetings`,
  );
  return response.data.result;
};

// Edit Meeting
export const editMeeting = async (
  data: { organizationId: string; meetingId: string },
  updatedData: any,
) => {
  const response = await axiosInstance.put<V2Response<any>>(
    `/organizations/${data.organizationId}/meetings/${data.meetingId}`,
    updatedData,
  );
  return response.data.result;
};

// ----------------- [ JOB SEEKERS API ]
export const getJobSeekers = async (data: {
  query: string;
  filters: {
    skills?: string[];
    experience?: { min?: number; max?: number };
    work_preference?: ("onsite" | "remote" | "hybrid")[];
    locations?: { country?: string; state?: string; city?: string }[];
    /** The search origin: coordinates, or the place a ZIP resolved to (the service geocodes it). */
    geo?: {
      lng?: number;
      lat?: number;
      city?: string;
      state?: string;
      country?: string;
      radius: number;
    };
  };
  options: { page: number; per_page: number };
}) => {
  const response = await axiosInstance.post<V2Response<JobseekerSearchResult>>(`/jobseekers`, data);
  return response.data.result;
};

// country state city
export const getAllCountry = async () => {
  const response = await axiosInstance.get<V2Response<any>>("/countries");
  return response.data.result;
};

export const getAllState = async (countryCode: string) => {
  const response = await axiosInstance.get<V2Response<any>>(`/countries/${countryCode}/states`);
  return response.data.result;
};

export const getAllCity = async (countryCode: string, stateCode: string) => {
  const response = await axiosInstance.get<V2Response<any>>(
    `/countries/${countryCode}/states/${stateCode}/cities`,
  );
  return response.data.result;
};

// notification

export const deleteNotificationById = async (
  organizationId: string | null,
  userId: string | null,
  notificationId: string,
) => {
  const response = await axiosInstance.delete<V2Response<any>>(
    `${organizationId ? `/organizations/${organizationId}` : `/users/${userId}`}/notifications/${notificationId}`,
  );
  return response.data.result;
};

export const readNotifications = async (userId: string) => {
  const response = await axiosInstance.patch<V2Response<any>>(
    `/users/${userId}/notifications/read-all`,
  );
  return response.data.result;
};

export const acceptOfferNotifications = async (jobId: string, offerId: string) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/jobs/${jobId}/offer/${offerId}/accept`,
  );
  return response.data.result;
};

export const declineOfferNotifications = async (jobId: string, offerId: string) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/jobs/${jobId}/offer/${offerId}/decline`,
  );
  return response.data.result;
};

export const acceptInterviewNotifications = async (organizationId: string, meetingId: string) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/organizations/${organizationId}/meetings/${meetingId}/accept`,
  );
  return response.data.result;
};

export const declineInterviewNotifications = async (organizationId: string, meetingId: string) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/organizations/${organizationId}/meetings/${meetingId}/reject`,
  );
  return response.data.result;
};

export const deleteFcmToken = async (userId: string) => {
  const token = localStorage.getItem("fcmToken");
  await axiosInstance.delete<V2Response<any>>(`/users/${userId}/fcm-token/${token}`);
  // Preserve the "last used login method" hint across logout so the login
  // page can still surface it for returning users.
  const lastLoginMethod = localStorage.getItem(LAST_LOGIN_METHOD_KEY);
  localStorage.clear();
  if (lastLoginMethod) {
    localStorage.setItem(LAST_LOGIN_METHOD_KEY, lastLoginMethod);
  }
};

export const postLogOut = async (refresh_token: string) => {
  await axiosInstance.post<V2Response<any>>(`/auth/logOut`, {
    refresh_token,
  });
};

// accounts

// get single user data

export const deleteMeeting = async ({
  organizationId,
  meetingId,
}: {
  organizationId: string;
  meetingId: string;
}) => {
  const response = await axiosInstance.delete<V2Response<any>>(
    `/organizations/${organizationId}/meetings/${meetingId}`,
  );
  return response.data.result;
};

// shortlist application
export const shortlistJobApplication = async (jobId: string, applicationId: string) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/jobs/${jobId}/applications/${applicationId}/shortlist`,
  );
  return response.data.result;
};

export const addToStarred = async (id: string, userId: string) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/organizations/${id}/favourites/${userId}`,
  );
  return response.data.result;
};

export const deleteApplicant = async (jobId: string, appliId: string, headers: any) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/jobs/${jobId}/applications/${appliId}/delete`,
    {},
    headers,
  );
  return response.data.result;
};

export const updateOnboardingStep = async (userId: string, data: any) => {
  const response = await axiosInstance.patch<V2Response<any>>(`/users/${userId}/onboarding`, data);
  return response.data.result;
};

export const getOnboardingStats = async (userId: string) => {
  const response = await axiosInstance.get<V2Response<any>>(`/users/${userId}/onboarding`);
  return response.data.result;
};

export const changePassword = async (userId: string, data: any) => {
  const response = await axiosInstance.put<V2Response<any>>(`/users/${userId}/password`, data);
  return response.data.result;
};

export { updateUser } from "@/api/user";

export const uploadUserVideo = async (userId: string) => {
  const response = await axiosInstance.post<V2Response<any>>(`/users/${userId}/video`);
  return response.data.result;
};

export const deleteUserVideo = async (userId: string) => {
  const response = await axiosInstance.delete<V2Response<any>>(`/users/${userId}/video`);
  return response.data.result;
};

export const updateUserProfleDetails = async (userID: string, data: any) => {
  const response = await axiosInstance.put<V2Response<any>>(`/users/${userID}`, data);
  return response.data.result;
};

export const checkEmailExist = async (data: { email: string }) => {
  const response = await axiosInstance.post<V2Response<{ status: boolean }>>(
    "/users/email-exists",
    data,
  );
  return response.data.result;
};

export const getUserInformation = async (userId: string) => {
  const response = await axiosInstance.get<V2Response<any>>(`/users/${userId}`);
  return response.data.result;
};

// ContactUs api

export const ContactUsAPI = async (values: any) => {
  const response = await axiosInstance.post<V2Response<any>>("/other/contact-us", values);
  return response.data.result;
};

// seetting api

export const getUserSettings = async (id: string) => {
  const response = await axiosInstance.get<V2Response<any>>(`/users/${id}/settings`);
  return response.data.result;
};

export const updateUserSettings = async (id: string, payload: any) => {
  const response = await axiosInstance.put<V2Response<any>>(`/users/${id}/settings`, payload);
  return response.data.result;
};

/** Same implementation as `@/api/subscription` (normalized `msLeft`, billing migration fields, etc.). */
export { getSubscriptionDetails } from "./subscription";

// Schedule Interview

export const getAllMeetingsTimes = async (userId: string) => {
  const response = await axiosInstance.get<V2Response<any>>(`/users/${userId}/meetings`);
  return response.data.result;
};

export const scheduleInterview = async (organizationId: string, data: any) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/organizations/${organizationId}/meetings`,
    data,
  );
  return response.data.result;
};

// job seeker job screen Apis

export const getJobPostData = async (queryParams: string) => {
  const response = await axiosInstance.get<V2Response<any>>(`/jobs/search-2?${queryParams}`);
  return response.data.result;
};

export const archieveJob = async (id: string, jobId: string) => {
  const response = await axiosInstance.post<V2Response<any>>(`users/${id}/archived/${jobId}`);
  return response.data.result;
};

// job Seeker Apply Apis

export const createApplication = async (jobId: string, data: any) => {
  const response = await axiosInstance.post<V2Response<any>>(`/jobs/${jobId}/applications`, data);
  return response.data.result;
};

export const generateUploadUrl = async (fileName: string) => {
  const response = await axiosInstance.get<V2Response<any>>("/uploads/upload-presigned-url", {
    params: {
      file_name: fileName,
    },
  });
  return response.data.result;
};

// job seeker side
export const getAppliedJobs = async (userId: string) => {
  const response = await axiosInstance.get<V2Response<any>>(
    `/users/${userId}/applications?sort=new_to_old`,
  );
  return response.data.result;
};

export const applicationReview = async (applicationId: string) => {
  const response = await axiosInstance.get<V2Response<JobApplication>>(
    `/jobs/applications/${applicationId}`,
  );
  return response.data.result;
};

export const getArchivedJobs = async (userId: string) => {
  const response = await axiosInstance.get<V2Response<any>>(`/users/${userId}/archived`);
  return response.data.result;
};

export const unArchivedJob = async (userId: string, jobId: string) => {
  const response = await axiosInstance.delete<V2Response<any>>(
    `/users/${userId}/archived/${jobId}`,
  );
  return response.data.result;
};

export const getInterview = async (userId: string) => {
  const response = await axiosInstance.get<V2Response<any>>(`/users/${userId}/meetings`);
  return response.data.result;
};

export const generatePresignedUploadUrl = async (fileName: string) => {
  const response = await axiosInstance.get<V2Response<any>>(
    `/uploads/upload-presigned-url?file_name=${encodeURIComponent(fileName)}`,
  );
  return response.data.result;
};

export const cancelSubscriptions = async (orgID: string) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `organizations/${orgID}/subscription/cancel`,
  );
  return response.data.result;
};

// Delete Account
export const deleteAccount = async (id: string) => {
  const response = await axiosInstance.post<V2Response<any>>(`/users/${id}/delete`);
  return response.data.result;
};

// Upload Video for Job Post
export const uploadJobPostVideo = async (jobId: string, data: any) => {
  const response = await axiosInstance.patch<V2Response<any>>(`/jobs/${jobId}/video`, data);
  return response.data.result;
};

interface SuggestionItem {
  title: string;
  score: number;
  source: string;
}

interface SuggestionsV2Response {
  data: SuggestionItem[];
  meta: {
    took_ms: number;
    total: number;
    limit: number;
  };
}

export const getSuggestionsNew = async (query: string) => {
  // Encode the query so multi-word titles ("software engineer") and special
  // characters ("c++", "r&d") produce a valid URL instead of a malformed one
  // that returns wrong/empty suggestions.
  const response = await anyAxios.get<SuggestionsV2Response>(
    `${SUGGESTIONS_URL}/v2/suggestions?q=${encodeURIComponent(query)}`,
  );
  // Map V2 response to return array of title strings for backward compatibility
  return { data: response.data.data.map((item) => item.title) };
};
